import React, { useState } from "react";
import { EditOutlined, Add } from "@mui/icons-material";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {  useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EnableDisableButton from "components/Shared/EnableDisableButton";
import DeliveryCollectionBasicDetail from "./DeliveryCollectionComponents/DeliveryCollectionBasicDetail";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import DeliveryCollectionFinanceForm from "./FinanceCollectionDelivery/FinanceCollectionforDeliveryCollection";
import {
  useGetDeliveryCollectionDetailQuery,
  useUpdateDeliveryCollectionMutation,
  useAddFinanceDeliveryCollectionMutation,
  useRejectDeliveryCollectionMutation,
} from "store/services/deliveryCollectionApi";
import {
  DeliveryCollectionDetailContext,
  useDeliveryCollectionDetailContext,
} from "contexts/PaymentContexts/DeliveryCollectionDetailContext";
import DeliveryCollectionDetailTab from "./DeliveryCollectionComponents/DeliveryCollectionDetailTab";
import { useAuth } from "hooks";

const DeliveryCollectionDetailScreen = () => {
  const { id } = useParams();

  const breadcrumb = [
    {
      title: "Delivery Collections",
      path: "/app/crm/delivery-collection/list",
    },
    { title: "Delivery Collection Detail" },
  ];

  const {
    data: deliveryCollectionDetail,
    isLoading: loadingDeliveryCollection,
    error,
  } = useGetDeliveryCollectionDetailQuery(id);

  return (
    <DeliveryCollectionDetailContext.Provider
      value={{ deliveryCollectionDetail, loadingDeliveryCollection }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumb}
        isBusy={loadingDeliveryCollection}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <DeliveryCollectionBasicDetail />
          <Divider />
          <DeliveryCollectionDetailTab />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </DeliveryCollectionDetailContext.Provider>
  );
};

export default DeliveryCollectionDetailScreen;

const PageActions = () => {
  
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const { deliveryCollectionDetail, loadingDeliveryCollection } =
    useDeliveryCollectionDetailContext();

  const [updateDeliveryCollection, { isLoading: updatingDeliveryCollection }] =
    useUpdateDeliveryCollectionMutation();
  const [
    addFinanceDeliveryCollection,
    { isLoading: addingFinanceDeliveryCollection },
  ] = useAddFinanceDeliveryCollectionMutation();
  const [rejectDeliveryCollection, { isLoading: rejectingDeliveryCollection }] =
    useRejectDeliveryCollectionMutation();

  const handleToggleStatus = async () => {
    try {
      await updateDeliveryCollection({
        _id: deliveryCollectionDetail._id,
        isActive: !deliveryCollectionDetail.isActive,
      }).unwrap();
      toast.info(
        `Delivery Collection ${
          deliveryCollectionDetail.isActive ? "Disabled" : "Enabled"
        }!`
      );
    } catch (error) {
      toast.error(error?.data?.message ?? "Could Not Update Status");
    }
  };

  const handleFinanceCollectionSubmit = async (payload) => {
    try {
      await addFinanceDeliveryCollection({
        id: deliveryCollectionDetail._id,
        ...payload,
      }).unwrap();
      toast.success("Finance delivery collection added successfully");
      setOpenModal(false);
    } catch (error) {
      toast.error(
        error?.data?.message ?? "Failed to add finance delivery collection"
      );
    }
  };

  const handleRejectCollection = async () => {
    try {
      await rejectDeliveryCollection(deliveryCollectionDetail._id).unwrap();
      toast.success("Delivery collection rejected successfully");
      setOpenRejectDialog(false);
      setAnchorEl(null);
    } catch (error) {
      toast.error(
        error?.data?.message ?? "Failed to reject delivery collection"
      );
    }
  };
  const { isSystemAdmin, isAccountAdmin, isEntityAdmin } = useAuth();

  const collectButtonLabel =
    deliveryCollectionDetail?.collectStatus === "Rejected"
      ? "Rejected"
      : deliveryCollectionDetail?.collectStatus === "Approved"
      ? "Approved"
      : "Manage Collection";

  const isCollectButtonDisabled =
    loadingDeliveryCollection ||
    addingFinanceDeliveryCollection ||
    rejectingDeliveryCollection ||
    deliveryCollectionDetail?.collectStatus === "Rejected" ||
    deliveryCollectionDetail?.collectStatus === "Approved";

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCollectClick = () => {
    setOpenModal(true);
    setAnchorEl(null);
  };

  const handleRejectClick = () => {
    setOpenRejectDialog(true);
    setAnchorEl(null);
  };

  return (
    <>
      <Box display="flex" gap={2} alignItems="center">
        {isAccountAdmin &&( <EnableDisableButton
          isActive={deliveryCollectionDetail?.isActive}
          loading={loadingDeliveryCollection || updatingDeliveryCollection}
          onClick={handleToggleStatus}
        />)}
        {/* <Link
          to={`/app/crm/delivery-collection/${deliveryCollectionDetail?._id}/edit`}
        >
          <Button startIcon={<EditOutlined />} variant="contained">
            Edit
          </Button>
        </Link> */}
        <Box position="relative">
          {(isSystemAdmin || isAccountAdmin || isEntityAdmin) && (
            <Button
  startIcon={<Add fontSize="small" />}
  variant="contained"
  color="primary"
  onClick={handleMenuOpen}
  disabled={isCollectButtonDisabled}
  sx={{
    // minWidth: "130px",
    // height: 32,
    borderRadius: "5px",
    textTransform: "none",
    fontSize: "1rem",
    px: 1.5,
    py: 0.5,
  }}
>
  {collectButtonLabel}
</Button>

          )}
   <Menu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={handleMenuClose}
  PaperProps={{
    elevation: 4,
    sx: {
      borderRadius: 2,
      minWidth: 160,
      mt: 1,
      p: 0.5,
    },
  }}
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "right",
  }}
  transformOrigin={{
    vertical: "top",
    horizontal: "right",
  }}
>
  <MenuItem
    onClick={handleCollectClick}
    sx={{ borderRadius: 1, fontSize: "1rem", minHeight: 36 }}
  >
    <Add fontSize="small" sx={{ mr: 1 }} />
    Approve
  </MenuItem>

  <MenuItem
    onClick={handleRejectClick}
    sx={{ borderRadius: 1, fontSize: "1rem", minHeight: 36 }}
  >
    <EditOutlined fontSize="small" sx={{ mr: 1 }} />
    Reject
  </MenuItem>
</Menu>


        </Box>
      </Box>

      <BaseModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Collect Delivery Amount"
      >
        <DeliveryCollectionFinanceForm
          onSubmit={handleFinanceCollectionSubmit}
          isBusy={addingFinanceDeliveryCollection}
          onCancel={() => setOpenModal(false)}
        />
      </BaseModal>

      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description"
      >
        <DialogTitle id="reject-dialog-title">Confirm Reject</DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-dialog-description">
            Are you sure you want to reject this delivery collection?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleRejectCollection} color="primary" autoFocus>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
